import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { CompanyThumbnail } from '../companies';
import {
  Button,
  Container,
  Grid,
  KeyValue,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  Popover,
  Section,
  TabSection,
  Tag,
  PageLoader,
  PageHeader,
  Heading,
  Icon,
  majorScale,
  defaultFills,
  Spinner,
  Table,
  EditableText,
  useAlert,
  useToast,
} from '../../npaper';

import { SalesTable } from '../sales';
import { ContactDetailsDialog } from './ContactDetailsDialog';
import { IContact, useStores } from '../../stores';
import Box from 'ui-box';
import { observer, Observer } from 'mobx-react-lite';
import { CompanySelect } from '../companies/CompanySelect';
import { IRelation } from '../../stores/contacts/RelationStore';
import { IQueryCache } from '../../stores/utils/modelWithCRUD';
import { UserThumbnail } from '../settings/UserThumbnail';
import { FormattedMessage, useIntl } from 'react-intl';
import { TimeLine } from '../time-line/TimeLine';
import { ContactGroupSelect } from '../groups/ContactGroupSelect';
import { ContactGroupsTable } from '../groups/ContactGroupsTable';

export const ContactPage = observer(() => {
  const match = useRouteMatch<{ id: string }>();
  const id = match.params.id;

  const [contact, setContact] = useState<IContact>();
  const [notFound, setNotFound] = useState(false);

  const { contactStore, relationStore, contactGroupStore } = useStores();
  const history = useHistory();
  const intl = useIntl();
  const toaster = useToast();
  const deleteAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Deleting contact',
      description: 'Delete contact alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to delete this contact? This cannot be undone.',
      description: 'Delete contact alert message',
    }),
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Delete',
      description: 'Delete contact alert message confirm btn text',
    }),
    intent: 'danger',
    icon: 'warning-sign',
  });

  useEffect(() => {
    let isSub = true;
    setContact(undefined);
    contactStore
      .getItem(Number(id))
      .then((contact) => isSub && setContact(contact))
      .catch(() => setNotFound(true));
    return () => {
      isSub = false;
    };
  }, [contactStore, id]);

  const [showDetails, setShowDetails] = useState(false);

  return !contact ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={majorScale(2)}
            color={defaultFills.danger[500]}
          />
          <Heading size={400}>
            <FormattedMessage defaultMessage="Could not find contact" />
          </Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer>
      <ContactDetailsDialog
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
        contact={contact}
      />

      <Container>
        <PageHeader
          title={contact.name}
          type="Kontaktperson"
          typeIcon="person"
          actions={
            <Popover
              placement="bottom-end"
              content={(close) => (
                <Menu>
                  <MenuDivider text="Handlinger" />
                  <MenuItem
                    text="Rediger detaljer"
                    onClick={() => {
                      close();
                      setShowDetails(true);
                    }}
                  />
                  <MenuDivider />
                  <MenuItem
                    text="Slet kontaktperson"
                    intent="danger"
                    icon="trash"
                    onClick={() => {
                      close();
                      deleteAlert.show(async () => {
                        try {
                          await contactStore.deleteItem(contact);
                          history.push(`/contacts`);
                        } catch (err) {
                          toaster.addToast({
                            message: intl.formatMessage({
                              defaultMessage:
                                'Could not delete contact. Please try again',
                              description:
                                'Could not delete contact error message',
                            }),
                          });
                        }
                      });
                    }}
                  />
                </Menu>
              )}
            >
              <Button iconRight="chevron-down">Handlinger</Button>
            </Popover>
          }
          id={id}
          thumbnail={contact.thumbnail}
          thumbnailRound={true}
          thumbnailPlaceholder={contact.initials}
        />

        <Grid>
          <div className="col-span-4">
            <Section
              title="Detaljer"
              actions={
                <Button
                  minimal={true}
                  iconRight="edit"
                  onClick={() => setShowDetails(true)}
                >
                  Rediger
                </Button>
              }
            >
              <KeyValue keyName="Navn" value={contact.name} />
              <KeyValue keyName="Emails" value={contact.emails.join(', ')} />
              <KeyValue keyName="Telefon" value={contact.phone} />
              <KeyValue keyName="Mobil" value={contact.mobile} />
            </Section>

            <Section
              title="Administrering"
              actions={
                <Button
                  iconRight="edit"
                  minimal={true}
                  onClick={() => setShowDetails(true)}
                >
                  Rediger
                </Button>
              }
            >
              <KeyValue keyName="Reference" value={contact.reference} />
              <KeyValue keyName="Beskrivelse" value={contact.description} />
              <KeyValue
                keyName="Ansvarlige"
                value={
                  contact.responsible.length > 0 ? (
                    contact.responsible.reduce((p, n) => p && !!n, true) ? (
                      contact.responsible.map((member, i) => (
                        <UserThumbnail
                          user={member!.user}
                          key={member!.id}
                          showName
                          marginRight={majorScale(2)}
                        />
                      ))
                    ) : (
                      <Spinner size={16} />
                    )
                  ) : undefined
                }
              />
              <KeyValue
                keyName="Tags"
                value={
                  contact.tags.length > 0
                    ? contact.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Section>
          </div>

          <div className="col-span-8">
            <TabSection
              sections={[
                {
                  title: 'Virksomheder',
                  actions: (
                    <CompanySelect
                      onSelect={(company) =>
                        relationStore
                          .createItem({
                            contact: contact.id,
                            company: company.id,
                          })
                          .catch((err) => {
                            if (err.response && err.response.status === 400) {
                              toaster.addToast({
                                message: intl.formatMessage(
                                  {
                                    defaultMessage:
                                      'Contact is already in {companyName}',
                                  },
                                  {
                                    companyName: company.name,
                                  }
                                ),
                                intent: 'warning',
                              });
                            } else {
                              toaster.showDefaultError(err);
                            }
                          })
                      }
                    >
                      <Button iconRight="link" minimal={true}>
                        Knyt til virksomhed
                      </Button>
                    </CompanySelect>
                  ),
                  content: <RelatedCompaniesTable contact={contact} />,
                },
                {
                  title: 'Salg',
                  actions: (
                    <Button iconRight="add" minimal={true}>
                      Opret salg
                    </Button>
                  ),
                  content: (
                    <Observer>
                      {() => <SalesTable filter={{ contact: contact.id }} />}
                    </Observer>
                  ),
                },
                {
                  title: intl.formatMessage({ defaultMessage: 'Groups' }),
                  actions: (
                    <>
                      <ContactGroupSelect
                        placement="bottom-end"
                        onSelect={(group) => {
                          contactGroupStore.manageContactsAndCompanies(group, {
                            contacts: [contact],
                          });
                        }}
                      >
                        <Button iconRight="link" minimal={true}>
                          <FormattedMessage defaultMessage="Add to group" />
                        </Button>
                      </ContactGroupSelect>
                    </>
                  ),
                  content: (
                    <ContactGroupsTable
                      marginTop={majorScale(-2)}
                      hideHeader
                      filter={{ filter: { contacts__in: [id] } }}
                      onRemove={(group) => {
                        contactGroupStore.manageContactsAndCompanies(group, {
                          contacts: [contact],
                          deleteItems: true,
                        });
                      }}
                    />
                  ),
                },
              ]}
            />
          </div>

          <div className="col-span-12">
            <Section title={intl.formatMessage({ defaultMessage: 'Timeline' })}>
              <TimeLine
                timelineBaseUrl={`${contactStore.apiPath}/${contact.id}/timeline/`}
              />
            </Section>
          </div>
        </Grid>
      </Container>
    </PageContainer>
  );
});

const RelatedCompaniesTable = observer(({ contact }: { contact: IContact }) => {
  const { relationStore } = useStores();
  const toaster = useToast();

  const [needsMore, setNeedsMore] = useState(true);

  const [res, setRes] = useState<IQueryCache<IRelation>>();

  const removeCompanyAlert = useAlert({
    message:
      'Er du sikker på at du vil fjerne kontaktpersonen fra denne virksomhed?',
    intent: 'danger',
    confirmButtonText: 'Fjern fra virksomhed',
  });

  useEffect(() => {
    let isSub = true;
    relationStore.fetchMore({ filter: { contact: contact.id } }).then((res) => {
      if (isSub) {
        setRes(res);
        setNeedsMore(false);
      }
    });
    return () => {
      isSub = false;
    };
  }, [relationStore, contact, needsMore]);

  return !res || !res.items || res.isLoading ? (
    <Spinner size={16} />
  ) : res.items.length > 0 ? (
    <Table
      marginTop={majorScale(-2)}
      onScrollNearBottom={() => setNeedsMore(true)}
    >
      <tbody>
        {res.items.map((relation) => (
          <tr key={relation.id}>
            {relation.company ? (
              <>
                <td>
                  <CompanyThumbnail company={relation.company} />
                </td>
                <td>
                  <EditableText
                    value={relation.title || ''}
                    placeholder="Titel"
                    maxWidth="200px"
                    onConfirm={(title) =>
                      relationStore.updateItem(relation, { title: title })
                    }
                  />
                </td>
                <td style={{ width: '32px' }}>
                  <Button
                    minimal
                    iconRight="trash"
                    onClick={() =>
                      removeCompanyAlert.show(() =>
                        relationStore
                          .deleteItem(relation)
                          .catch(toaster.showDefaultError)
                      )
                    }
                  />
                </td>
              </>
            ) : (
              <td colSpan={100}>
                <Box className="skeleton" height="32px"></Box>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  ) : (
    <Box is="p">Ikke tilknyttet nogen virksomheder</Box>
  );
});
